import React, { useEffect, useState } from 'react';
import { BellRing, BellOff, Check, Loader2 } from 'lucide-react';
import { vehiclesAPI, authAPI } from '../services/api';
import { useT } from '../services/i18n';

const LS = 'vehicleAlerts';
const localHas = (k) => { try { return (JSON.parse(localStorage.getItem(LS)) || []).includes(k); } catch { return false; } };
const localSet = (k, on) => { const l = (() => { try { return JSON.parse(localStorage.getItem(LS)) || []; } catch { return []; } })().filter((x) => x !== k); localStorage.setItem(LS, JSON.stringify(on ? [k, ...l] : l)); };

// « M'alerter des nouvelles pièces » on a vehicle model page (guest = e-mail form, logged-in = one tap)
const VehicleAlertButton = ({ brandSlug, modelSlug, label, engine = null }) => {
  const { t, lang } = useT();
  const authed = authAPI.isAuthenticated();
  const key = `${brandSlug}/${modelSlug}${engine ? `#${engine}` : ''}`;
  const [scope, setScope] = useState(engine ? 'engine' : 'model');
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [state, setState] = useState('idle');
  const [subscribed, setSubscribed] = useState(() => localHas(key));

  useEffect(() => {
    setSubscribed(localHas(key) || localHas(`${brandSlug}/${modelSlug}`)); setState('idle'); setOpen(false); setScope(engine ? 'engine' : 'model');
    if (authed) vehiclesAPI.alertStatus(brandSlug, modelSlug, engine).then((d) => { setSubscribed(!!d.subscribed); localSet(key, !!d.subscribed); }).catch(() => {});
  }, [authed, brandSlug, modelSlug, key, engine]);

  const submit = async (e) => {
    e?.preventDefault();
    setState('loading');
    try {
      const eng = scope === 'engine' ? engine : null;
      const d = await vehiclesAPI.alertSubscribe(brandSlug, modelSlug, authed ? null : email.trim(), lang, frequency, eng);
      localSet(eng ? key : `${brandSlug}/${modelSlug}`, true); setSubscribed(true); setState(d.already ? 'already' : 'done');
    } catch { setState('error'); }
  };
  const cancel = async () => {
    setState('loading');
    try { await vehiclesAPI.alertRemove(brandSlug, modelSlug, engine); localSet(key, false); localSet(`${brandSlug}/${modelSlug}`, false); setSubscribed(false); setState('idle'); } catch { setState('error'); }
  };

  if (subscribed) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2" data-testid="vehicle-alert-subscribed">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1.5"><Check size={14} /> {state === 'done' ? t('val.activated', { f: frequency === 'weekly' ? t('val.weeklyShort') : t('val.instantShort'), e: scope === 'engine' && engine ? ` · ${engine}` : '' }) : state === 'already' ? t('val.already') : t('val.active')}</span>
        {authed && <button type="button" onClick={cancel} className="text-xs text-gray-500 hover:text-red-600 underline" data-testid="vehicle-alert-cancel"><BellOff size={12} className="inline mr-1" />{t('val.disable')}</button>}
      </span>
    );
  }
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} disabled={state === 'loading'} data-testid="vehicle-alert-button"
        className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#1e3a5f] hover:bg-[#162c48] px-4 py-1.5 rounded-full transition-colors disabled:opacity-60">
        {state === 'loading' ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />} {label || (engine ? `${t('val.cta')} (${engine})` : t('val.cta'))}
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="w-full rounded-lg border border-blue-200 bg-blue-50 p-3 max-w-md" data-testid="vehicle-alert-form">
      <p className="text-sm font-semibold text-[#1e3a5f] flex items-center gap-1.5"><BellRing size={14} /> {t('val.formTitle')}</p>
      <div className="flex gap-2 mt-2">
        {!authed && <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('val.emailPh')} data-testid="vehicle-alert-email" className="flex-1 h-9 text-sm border border-gray-300 rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-orange-300" />}
        <button type="submit" disabled={state === 'loading'} data-testid="vehicle-alert-submit" className="h-9 px-4 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">{state === 'loading' && <Loader2 size={14} className="animate-spin" />} {t('val.enable')}</button>
      </div>
      {engine && (
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-700" data-testid="vehicle-alert-scope">
          <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="radio" name="vehicle-alert-scope" value="engine" checked={scope === 'engine'} onChange={() => setScope('engine')} data-testid="vehicle-alert-scope-engine" className="accent-orange-500" /> {t('val.onlyEngine')} <span className="font-semibold">{engine}</span></label>
          <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="radio" name="vehicle-alert-scope" value="model" checked={scope === 'model'} onChange={() => setScope('model')} data-testid="vehicle-alert-scope-model" className="accent-orange-500" /> {t('cm.allEngines')}</label>
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-700" data-testid="vehicle-alert-frequency">
        <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="radio" name="vehicle-alert-frequency" value="weekly" checked={frequency === 'weekly'} onChange={() => setFrequency('weekly')} data-testid="vehicle-alert-frequency-weekly" className="accent-orange-500" /> {t('val.weekly')}</label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="radio" name="vehicle-alert-frequency" value="instant" checked={frequency === 'instant'} onChange={() => setFrequency('instant')} data-testid="vehicle-alert-frequency-instant" className="accent-orange-500" /> {t('val.instant')}</label>
      </div>
      {state === 'error' && <p className="text-xs text-red-600 mt-2" data-testid="vehicle-alert-error">{t('val.error')}</p>}
    </form>
  );
};

export default VehicleAlertButton;
